import { apiRequest } from '../../../utils/api';
import { AreaGeometry, AreaLevel, InViewArea, NamedArea } from './types';
import { STANDIN_DISTRICTS } from './districtsStandin';
import * as polygonClipping from 'polygon-clipping';

// Shape the JAA-276 resolution/search endpoints return per area. Kept permissive
// (optional fields) because geometry can be absent on a degraded result, and we
// tolerate the older `wof_id` key from a stand-in.
interface RawArea {
  name?: string;
  ext_id?: string | number;
  wof_id?: string | number;
  level?: AreaLevel;
  // Geometry and bbox come from the service but are not forwarded to the POST
  // payload — the backend resolves geometry from ext_id server-side (JAA-278).
  geometry?: AreaGeometry | null;
  bbox?: [number, number, number, number];
}

// Normalize a raw service area into a NamedArea pick, or null when it lacks a
// usable name OR a stable id — an id-less row would POST an unresolvable
// { ext_id: '' } the backend can't map to a polygon, so we drop it here.
const toArea = (raw: RawArea | null | undefined): NamedArea | null => {
  const name = (raw?.name ?? '').trim();
  if (!name) return null;
  const id = raw?.ext_id ?? raw?.wof_id;
  if (id == null || String(id).trim() === '') return null;
  return {
    kind: 'area',
    ext_id: String(id),
    name,
    level: raw?.level ?? 'district',
  };
};

// The zoom the map picker opens at. The prefilled own area and the nearby suggestions are
// resolved at it too, so they are the same areas the picker first draws.
export const AREA_OPENING_ZOOM = 13;

// A comparable key for an area id. Picks stored before the size-based picker carry a
// Geoapify place_id, which embeds the OSM id the backend now keys areas by, so both
// spellings of one area must compare equal (else editing an interest adds it twice).
export const areaIdKey = (extId: string): string => {
  const m = /f0010([0-9a-f])f901([0-9a-f]{16})/.exec(extId);
  if (!m) return extId;
  let id = 0;
  for (let byte = 7; byte >= 0; byte -= 1) {
    id = id * 256 + parseInt(m[2].slice(byte * 2, byte * 2 + 2), 16);
  }
  return `osm:${m[1]}:${id}`;
};

// Resolve the area containing a point (the seed building) at the picker's opening zoom
// via the BE Geoapify proxy (JAA-276). Returns null on any failure so prefill is best-effort.
export const resolveArea = async (lat: number, lng: number): Promise<NamedArea | null> => {
  try {
    const data = await apiRequest(
      `/neighborhoods/resolve?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}&zoom=${AREA_OPENING_ZOOM}`,
    );
    return toArea(data);
  } catch {
    return null;
  }
};

// Typeahead search for areas by name. Returns [] on any failure so the input
// stays usable even before JAA-276 ships.
export const searchAreas = async (query: string): Promise<NamedArea[]> => {
  const q = query.trim();
  if (q.length < 2) return [];
  try {
    const data = await apiRequest(`/neighborhoods/search?q=${encodeURIComponent(q)}`);
    const rows: RawArea[] = Array.isArray(data) ? data : data?.results ?? [];
    return rows.map(toArea).filter((a): a is NamedArea => a !== null);
  } catch {
    return [];
  }
};

// A map viewport bounding box in GeoJSON [minLng, minLat, maxLng, maxLat] order,
// matching the JAA-276 /neighborhoods/in-view contract.
export type Bbox = [number, number, number, number];

// Normalize a raw service area into an InViewArea for the map picker, requiring a
// usable name AND a real Polygon/MultiPolygon geometry. Returns null otherwise so
// degraded rows (bbox-only, geometry-less) are dropped rather than drawn empty.
const toInViewArea = (raw: RawArea | null | undefined): InViewArea | null => {
  const base = toArea(raw);
  if (!base) return null;
  const geometry = raw?.geometry;
  if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) return null;
  const bbox = raw?.bbox ?? bboxOfGeometry(geometry);
  return { ...base, geometry, bbox };
};

// One in-view answer: the areas to draw, and whether asking again could add more (the
// backend probes a bounded number of points per request and loads the rest on repeat).
export interface InViewResult {
  areas: InViewArea[];
  complete: boolean;
}

// The areas to draw in the current map viewport. The backend picks, at each point, the
// largest admin area no bigger than the zoom's size threshold, so `zoom` decides the
// areas and `level` only labels them (older backends read the band). REJECTS on request
// failure: callers whose behaviour depends on the difference between "the backend
// confirmed this area is empty" and "the request failed" (JAA-342's auto-reveal must
// never spend its budget on an outage) need that distinction.
export const fetchAreasInView = async (
  bbox: Bbox,
  level: AreaLevel = 'district',
  zoom?: number,
): Promise<InViewResult> => {
  const param = bbox.map((n) => n.toFixed(6)).join(',');
  const zoomParam = zoom === undefined ? '' : `&zoom=${encodeURIComponent(zoom.toFixed(2))}`;
  const data = await apiRequest(
    `/neighborhoods/in-view?bbox=${encodeURIComponent(param)}&level=${encodeURIComponent(level)}${zoomParam}`,
  );
  const rows: RawArea[] = Array.isArray(data) ? data : data?.areas ?? [];
  return {
    areas: rows.map(toInViewArea).filter((a): a is InViewArea => a !== null),
    // A bare array comes from an older backend, whose answer was always whole.
    complete: Array.isArray(data) || data?.complete !== false,
  };
};

// Lenient variant: returns stand-in rows on any failure so display-only
// consumers (suggestion chips) degrade gracefully.
//
// DEV STAND-IN (remove when JAA-276 ships): the /neighborhoods/in-view endpoint
// does not exist yet, so a network/404 error falls through to a small set of real
// Helsinki district polygons (filtered to the viewport) so the picker is
// demonstrable now. Delete `HELSINKI_DISTRICTS_STANDIN`, `standInAreasInView`,
// and the catch fallback once the backend serves this route.
export const areasInView = async (
  bbox: Bbox,
  level: AreaLevel = 'district',
  zoom?: number,
): Promise<InViewArea[]> => {
  try {
    return (await fetchAreasInView(bbox, level, zoom)).areas;
  } catch {
    return standInAreasInView(bbox, level);
  }
};

// Districts near a point (the anchor building) to pre-surface as one-tap
// suggestion chips on the Location card (Figma "Expand your interest area":
// within 500m + nearby district names). Builds a small (~2 km) box around the
// point and reuses the in-view lookup, stripping picker-only geometry.
export const nearbyAreas = async (lat: number, lng: number, limit = 3): Promise<NamedArea[]> => {
  const d = 0.02;
  const bbox: Bbox = [lng - d, lat - d, lng + d, lat + d];
  const areas = await areasInView(bbox, 'district', AREA_OPENING_ZOOM);
  // Nearest first: the backend returns areas in no particular order, so Kallio was
  // offered Kaivopuisto and Etu-Toolo across the city before its neighbours. One extra
  // is returned because the caller drops the building's own area, which sorts first.
  // A degree of longitude is only cos(lat) of a degree of latitude (half at Helsinki).
  const lngScale = Math.cos((lat * Math.PI) / 180);
  const gap = ([minLng, minLat, maxLng, maxLat]: Bbox) =>
    Math.hypot(Math.max(minLng - lng, 0, lng - maxLng) * lngScale, Math.max(minLat - lat, 0, lat - maxLat));
  const centre = ([minLng, minLat, maxLng, maxLat]: Bbox) =>
    Math.hypot(((minLng + maxLng) / 2 - lng) * lngScale, (minLat + maxLat) / 2 - lat);
  return [...areas]
    .sort((a, b) => gap(a.bbox) - gap(b.bbox) || centre(a.bbox) - centre(b.bbox))
    .slice(0, limit + 1)
    .map(({ kind, ext_id, name, level }) => ({ kind, ext_id, name, level }));
};

// ── DEV STAND-IN (JAA-276) ─────────────────────────────────────────────────────
// Everything below is a temporary client-side fixture so the map picker can be
// built and demoed before the backend /neighborhoods/in-view route exists. Remove
// the entire block (and its call sites above) when JAA-276 ships.

// Axis-aligned bbox of a Polygon/MultiPolygon, in [minLng, minLat, maxLng, maxLat].
const bboxOfGeometry = (geometry: AreaGeometry): Bbox => {
  const rings =
    geometry.type === 'Polygon'
      ? (geometry.coordinates as number[][][])
      : (geometry.coordinates as number[][][][]).flat();
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  rings.flat().forEach(([lng, lat]) => {
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  });
  return [minLng, minLat, maxLng, maxLat];
};

// Build a closed rectangular [lng,lat] ring polygon from a bbox — good enough for
// the stand-in's blocky district shapes (real geometry arrives with JAA-276).
const rectPolygon = (
  minLng: number,
  minLat: number,
  maxLng: number,
  maxLat: number,
): AreaGeometry => ({
  type: 'Polygon',
  coordinates: [
    [
      [minLng, minLat],
      [maxLng, minLat],
      [maxLng, maxLat],
      [minLng, maxLat],
      [minLng, minLat],
    ],
  ],
});

// A handful of real Helsinki kaupunginosa-level districts as approximate blocky
// polygons, plus the Helsinki city polygon, so the picker has something to draw
// at both levels. Coordinates are GeoJSON [lng, lat].
// Real Helsinki kaupunginosa polygons (OSM, from districtsStandin.ts) so the
// picker draws true district shapes, not boxes.
const HELSINKI_DISTRICTS_STANDIN: InViewArea[] = STANDIN_DISTRICTS.map(({ ext_id, name, geometry }) => ({
  kind: 'area',
  ext_id,
  name,
  level: 'district',
  geometry,
  bbox: bboxOfGeometry(geometry),
}));

// City shape = union of the district fixtures, so it's the real land area rather
// than a bbox rectangle over the sea. Falls back to a bounding box if union fails.
const helsinkiCityGeometry: AreaGeometry = (() => {
  try {
    const polys = STANDIN_DISTRICTS.flatMap(({ geometry }) =>
      geometry.type === 'Polygon'
        ? [geometry.coordinates as number[][][]]
        : (geometry.coordinates as number[][][][]),
    );
    if (polys.length === 0) throw new Error('no district fixtures');
    const unioned = polygonClipping.union(
      ...(polys as unknown as [polygonClipping.Polygon, ...polygonClipping.Polygon[]]),
    ) as unknown as number[][][][];
    return { type: 'MultiPolygon', coordinates: unioned };
  } catch {
    return rectPolygon(24.78, 60.12, 25.25, 60.3);
  }
})();

const HELSINKI_CITY_STANDIN: InViewArea = {
  kind: 'area',
  ext_id: 'standin-helsinki',
  name: 'Helsinki',
  level: 'city',
  geometry: helsinkiCityGeometry,
  bbox: bboxOfGeometry(helsinkiCityGeometry),
};

// True when two bboxes overlap (so we only return stand-in areas in the viewport).
const bboxesIntersect = (a: Bbox, b: Bbox): boolean =>
  a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];

// The stand-in's areasInView: the fixture areas at `level` whose bbox overlaps
// the viewport.
const standInAreasInView = (bbox: Bbox, level: AreaLevel): InViewArea[] => {
  const pool = level === 'city' ? [HELSINKI_CITY_STANDIN] : HELSINKI_DISTRICTS_STANDIN;
  return pool.filter((area) => bboxesIntersect(area.bbox, bbox));
};
